import { gql } from "@apollo/client"
import { RestrictiveCovenantsSectionFragment } from "~/shared/components/FormSections/RestrictiveCovenantsSection/fragment"
import { EmploymentTemplateFragment } from "~/shared/fragments/EmploymentTemplateFragment"
import { ReimbursementBudgetFragment } from "~/shared/fragments/ReimbursementBudgetFragment"
import { VariableCompensationFragment } from "~/shared/fragments/VariableCompensationFragment"
import { RemoteWorkCostsFragment } from "~/shared/fragments/RemoteWorkCostsFragment"
import { CountrySalesFields } from "~/shared/fragments/CountrySalesFields"
import { Employment, Maybe } from "@oysterhr/schema"
import {
  ContractedTerminationNoticeFragment,
  ResignationNoticeFragment,
} from "~/shared/fragments/ContractedNoticeFragment"
import { RoleTypeFragment } from "~/shared/fragments/RoleTypeFragment"
import {
  HiringFTECompanyRegenerationInfoAlertFragment,
  HiringFTECompanyRegenerationInfoModalFragment,
} from "@oysterhr/hiring/full-time-employee"
import { MergeDeep } from "../utils/typeUtils"
import { MONEY_FIELDS } from "~/shared/fragments/MoneyFields"

export const HIRING_FORM_EMPLOYMENT_FIELDS_FRAGMENT = gql`
  ${MONEY_FIELDS}
  fragment HiringFormEmploymentFields on Employment {
    id
    personalInfo {
      fullLegalName
      email
      nationality
    }
    state
    countryCode
    subdivisionCode
    role
    seniorityLevel
    engagementType
    employmentType
    partTimeDaysPerWeek
    partTimeHoursPerDay
    fullTimeHoursPerWeek
    managerRole
    managerName
    startDate
    initialStartDate
    isPayrollTeamMember
    probationEnd
    endDate
    jobDescription
    minimumQualification
    dailyResponsibility
    primaryDuty
    decisionMakingAuthority
    salaryAmount
    salaryCurrencyCode
    hourlyRate
    lockedCurrencyCode
    bonusAmount
    bonusCurrencyCode
    providedEquipmentList
    providedEquipmentOther
    providedEquipmentPolicy
    providedEquipmentPolicyBudget {
      ...MoneyFields
    }
    reimbursementPolicy {
      policy
      amount {
        ...MoneyFields
      }
    }
    benefitsDescription
    privateNotes
    offerLetterSendRequested
    newHireStatus
    managerEmailAddress
    managerPhoneNumber
    managerIsExpenseApprover
    probationPeriodValue
    probationPeriodType
    probationPeriod
    paidHolidayDays
    commissionPlanOffered
    commissionPlan {
      id
      title
      description
      filename
    }
    equityOffered
    company {
      id
      countrySales {
        ...CountrySalesFields
      }
    }
    department {
      id
      name
    }
    company {
      id
      authorizations {
        actions
        fields
      }
    }
    suggestedBenefitPlans {
      id
      descriptiveName
      name
      provider {
        name
      }
      priceEstimate {
        currencies {
          ...MoneyFields
        }
      }
    }
    benefitsEnrollments {
      collection {
        id
        plan {
          id
        }
      }
    }
    benefitsProvisionedEnrollments {
      collection {
        id
        plan {
          id
        }
      }
    }
    hiring {
      id
      selfServiceOnboarding
      isAgreementESignEnabled
      agreements {
        id
        agreementType
        isAutomated
      }

      ...HiringFTECompanyRegenerationInfoAlertFragment
      ...HiringFTECompanyRegenerationInfoModalFragment
    }

    variableCompensations {
      nodes {
        ...VariableCompensationFragment
      }
    }

    visaDocumentUrl
    visaRequired

    visa {
      acquisionFee
      acquisionFeeCurrencyCode
      acquisitionPayer
      document {
        id
        title
        description
        filename
      }
      documentUrl
      expirationDate
      required
    }

    restrictiveCovenants {
      ...RestrictiveCovenantsSectionFragment
    }
    ...ResignationNoticeFragment
    workFocusedWithinCountryOfResidence

    employmentTemplate {
      ...EmploymentTemplateFragment
    }
    reimbursementBudgets {
      nodes {
        ...ReimbursementBudgetFragment
      }
    }
    ...RemoteWorkCostsFragment
    roleType {
      ...RoleTypeFragment
    }
    ...ContractedTerminationNoticeFragment
    cutoffMonthDay
    sickPay {
      customDurationUnit
      customDurationValue
      id
      option
    }
    latestSalaryCurrencyConversion {
      id
      exchangeRateValue
      expired
      receivedCurrencyCode
      receivedAmount
    }
    employmentComplianceCheck
    employmentComplianceDetails
    depositBeforeLiability
    workSchedules {
      day
      startTime
      endTime
    }
  }

  fragment HiringFTECompanyRegenerationInfoAlertFragment on Hiring {
    id
    agreements {
      id
      isAutomated
    }
  }

  fragment HiringFTECompanyRegenerationInfoModalFragment on Hiring {
    id
    agreements {
      id
      isAutomated
      agreementType
      lastUpdate {
        timestamp
      }
    }
  }

  ${ResignationNoticeFragment}
  ${ContractedTerminationNoticeFragment}
  ${RestrictiveCovenantsSectionFragment}
  ${VariableCompensationFragment}
  ${EmploymentTemplateFragment}
  ${ReimbursementBudgetFragment}
  ${RemoteWorkCostsFragment}
  ${CountrySalesFields}
  ${RoleTypeFragment}
`

export type HiringFormEmploymentFields = MergeDeep<
  MergeDeep<
    Pick<
      Employment,
      | "id"
      | "personalInfo"
      | "state"
      | "countryCode"
      | "subdivisionCode"
      | "role"
      | "roleType"
      | "seniorityLevel"
      | "engagementType"
      | "employmentType"
      | "partTimeDaysPerWeek"
      | "partTimeHoursPerDay"
      | "fullTimeHoursPerWeek"
      | "managerRole"
      | "managerName"
      | "startDate"
      | "initialStartDate"
      | "isPayrollTeamMember"
      | "probationEnd"
      | "endDate"
      | "jobDescription"
      | "salaryAmount"
      | "salaryCurrencyCode"
      | "lockedCurrencyCode"
      | "bonusAmount"
      | "bonusCurrencyCode"
      | "providedEquipmentList"
      | "providedEquipmentOther"
      | "providedEquipmentPolicy"
      | "providedEquipmentPolicyBudget"
      | "reimbursementPolicy"
      | "benefitsDescription"
      | "privateNotes"
      | "offerLetterSendRequested"
      | "newHireStatus"
      | "managerEmailAddress"
      | "managerPhoneNumber"
      | "managerIsExpenseApprover"
      | "probationPeriodValue"
      | "probationPeriodType"
      | "probationPeriod"
      | "paidHolidayDays"
      | "commissionPlanOffered"
      | "commissionPlan"
      | "equityOffered"
      | "company"
      | "department"
      | "suggestedBenefitPlans"
      | "benefitsEnrollments"
      | "benefitsProvisionedEnrollments"
      | "hiring"
      | "variableCompensations"
      | "visaDocumentUrl"
      | "visaRequired"
      | "visa"
      | "restrictiveCovenants"
      | "contractedTerminationNotice"
      | "resignationNotice"
      | "workFocusedWithinCountryOfResidence"
      | "reimbursementBudgets"
      | "remoteWorkCosts"
      | "cutoffMonthDay"
      | "employmentComplianceCheck"
      | "employmentComplianceDetails"
      | "depositBeforeLiability"
      | "hourlyRate"
    >,
    {
      employmentTemplate?: Maybe<EmploymentTemplateFragment>
    }
  >,
  {
    hiring: MergeDeep<
      HiringFTECompanyRegenerationInfoAlertFragment,
      HiringFTECompanyRegenerationInfoModalFragment
    >
  }
>
